import React from "react";
import { Box, Typography, Container, styled, SxProps, useTheme } from "@mui/material";
import Cover from "../../Components/Misc/Cover";
import { useParams } from "react-router";
import { isMobile } from "react-device-detect";
import { cacheService } from "../../Core/Api/services";
import { useTranslation } from "react-i18next";

export interface LogLayoutProps {
  children?: React.ReactNode;
  sxRightContainer?: SxProps;
  platformName?: string;
  catchSentence?: string | null;
}

const LogLayout: React.FC<LogLayoutProps> = ({ children, sxRightContainer = {}, platformName, catchSentence }) => {
  const { t } = useTranslation();
  const params = useParams();
  const brand = React.useMemo(() => params && params.brand && params.brand.length, [params]);
  const theme = useTheme();
  const brandName = cacheService.GetPlatformSettings()?.brandName;

  return (
    <Wrapper>
      <Cover
        img={{
          src: brand ? "/static/images/temp/log-brand-bg.jpg" : `/static/images/log-bg-${theme.palette.mode}.png`,
        }}
        sx={{ overflow: "hidden" }}
      />
      {brand && <Cover img={{ src: "/static/images/log-bg-layer.png" }} sx={{ overflow: "hidden" }} />}
      <Container
        sx={{
          width: "100%",
          gap: theme.spacing(8),
          padding: theme.spacing(8),
          overflowY: "scroll",
          [theme.breakpoints.down("md")]: {
            flexDirection: "column",
            gap: theme.spacing(4),
            padding: theme.spacing(4),
          },
        }}
      >
        <LeftContainer>
          {brand && <img src="/static/images/temp/log-logo-brand.png" width={192} alt="" />}
          <Typography
            variant={isMobile ? "h4" : "h3"}
            mb={1.5}
            mt={brand ? 1 : 0}
            fontWeight={400}
            component="h1"
            sx={{ color: "text.primary", opacity: "0.7" }}
          >
            {t("page:body.welcomeMessage", { platform: platformName ?? brandName ?? "mediaspot.io" })}
          </Typography>
          <Typography variant={isMobile ? "h2" : "h1"} component="h2">
            {catchSentence}
          </Typography>
        </LeftContainer>
        <RightContainer brand={brand} sx={sxRightContainer}>
          {children}
        </RightContainer>
      </Container>
    </Wrapper>
  );
};

export default LogLayout;

const Wrapper = styled(Box)(({ theme }) => ({
  height: "100vh",
  background: theme.palette.background.paper,
  ".MuiContainer-root": {
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    height: "100%",
  },
  ".MuiGrid-root": {
    height: "100%",
  },
}));
const LeftContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  paddingLeft: theme.spacing(8),
  [theme.breakpoints.down("md")]: {
    flexGrow: 1,
    paddingLeft: 0,
  },
}));

const RightContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "brand",
})<{ brand: number | string | undefined }>(({ theme, brand }) => ({
  height: "100%",
  width: 460,
  minHeight: 400,
  maxHeight: 700,
  borderRadius: 10,
  padding: `${theme.spacing(3)} ${theme.spacing(4)}`,
  background: theme.palette.mode === "light" ? theme.palette.background.paper : theme.palette.backgroundAlpha.main,
  border: `1px solid ${theme.palette.backgroundAlpha.border}`,

  [theme.breakpoints.down("md")]: {
    flexGrow: 1,
    width: "unset",
    height: "unset",
  },
}));
